import React from 'react';
import { Tooltip, Box, Button } from '@mui/material';
import { RateReview as AuditIcon } from '@mui/icons-material';
import { DataManagementConfig, TableColumn } from '@shared/components/data-management/types';
import { SurveyReportTableData, SurveyReportFormData } from '../types';
import { useSurveyReportsDataManagement } from '../hooks/useSurveyReportsForDataManagement';

export const useSimpleReportsConfig = (
  subjectId: string,
  tenantId: string,
  navigate?: (path: string) => void
): DataManagementConfig<SurveyReportTableData, SurveyReportFormData, SurveyReportFormData> => {
  const {
    useSurveyReportsQuery,
    useCreateSurveyReportWrapper,
    useUpdateSurveyReportWrapper,
    useDeleteSurveyReportWrapper,
  } = useSurveyReportsDataManagement(subjectId, tenantId);

  // View Report Action (only for generated reports)
  const viewReportAction = {
    label: 'View Report',
    onClick: (row: SurveyReportTableData) => {
      // Track report selection (TODO: Add PostHog hook if needed)

      if (navigate) {
        navigate(`/app/reports/${row.id}/view`);
      }
    },
    color: 'primary' as const,
    variant: 'contained' as const,
    condition: (row: SurveyReportTableData) => !!row.generatedAt, // Only show for generated reports
  };

  return {
    // Operations (hooks)
    hooks: {
      useQuery: useSurveyReportsQuery,
      useCreate: useCreateSurveyReportWrapper,
      useUpdate: useUpdateSurveyReportWrapper,
      useDelete: useDeleteSurveyReportWrapper,
    },

    table: {
      title: 'Survey Reports',
      columns: [
        {
          label: 'Status',
          key: 'generatedAt',
          render: (value: string | undefined) => {
            if (value) {
              return <Box sx={{ color: 'success.main', fontWeight: 'medium' }}>Published</Box>;
            }

            return (
              <Tooltip title="This report is waiting for the audit to be completed. Click 'Go to Audit' to complete the audit first.">
                <Box
                  sx={{
                    color: 'warning.main',
                    fontStyle: 'italic',
                    cursor: 'help',
                  }}
                >
                  Pending Audit
                </Box>
              </Tooltip>
            );
          },
        },
        {
          label: 'Survey',
          key: 'surveyTitle',
          render: (value: string | undefined) => value || 'Unknown Survey',
        },
        {
          label: 'Responses',
          key: 'includedResponses',
          render: (value: number) => {
            return `${value || 0}`;
          },
        },
        {
          label: 'Action',
          key: 'auditId',
          render: (auditId: string, row: SurveyReportTableData) => {
            // Only show "Go to Audit" for pending reports (no generatedAt)
            if (!row.generatedAt) {
              // Use row.auditId directly to ensure we have the correct value
              const actualAuditId = row.auditId || auditId;

              // If auditId is still undefined, show a disabled button with explanation
              if (!actualAuditId) {
                return (
                  <Tooltip title="Audit ID is missing. Cannot navigate to audit.">
                    <Box>
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        startIcon={<AuditIcon />}
                        disabled
                        sx={{ minWidth: 'auto' }}
                      >
                        Go to Audit
                      </Button>
                    </Box>
                  </Tooltip>
                );
              }

              return (
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  startIcon={<AuditIcon />}
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent row click

                    // Track audit navigation (TODO: Add PostHog hook if needed)

                    if (navigate) {
                      navigate(`/app/survey-audits/${actualAuditId}`);
                    }
                  }}
                  sx={{ minWidth: 'auto' }}
                >
                  Go to Audit
                </Button>
              );
            }
            // Empty cell for generated reports
            return null;
          },
        },
        {
          label: 'Date',
          key: 'generatedAt',
          render: (value: string | undefined) => {
            return value ? new Date(value).toLocaleDateString() : '';
          },
        },
      ] as TableColumn<SurveyReportTableData>[],
    },

    // Actions for each row
    actions: {
      custom: [viewReportAction], // Only keep View Report action, Go to Audit is now in dedicated column
    },

    // Features configuration
    features: {
      search: true,
      pagination: true,
      onRowClick: (row: SurveyReportTableData) => {
        // Only allow row click navigation for generated reports
        if (navigate && row.generatedAt) {
          // TODO: Add PostHog tracking via hooks when needed for row click navigation

          navigate(`/app/reports/${row.id}/view`);
        }
      },
    },
  };
};
